/**
 * useProposalJob hook for managing proposal generation
 * Uses the report job infrastructure
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  submitProposalJob, 
  getProposalTemplate, 
  getReportJobStatus,
  getProposalTemplateStream,
  ProposalTemplateStreamStatusData,
  ProposalTemplateStreamCompleteData,
  ProposalTemplateStreamErrorData,
} from '../api/reports';
import { ReportJobStatus } from '../types/api';
import { useToastStore } from '../stores/toast';

export interface ProposalTemplate {
  sections: Array<{ name: string; description: string }>;
  default_questions: Array<{ section: string; question: string }>;
  template_source: string;
  entity_types_by_question?: Record<string, string[]>; // Maps question text to entity types that will be queried
}

export type ProposalTemplateStage = 
  | 'idle' 
  | 'started' 
  | 'retrieval' 
  | 'retrieval_complete'
  | 'document_parsing'
  | 'structure_extraction'
  | 'structure_extraction_complete'
  | 'awaiting_approval'
  | 'section_mapping'
  | 'question_generation'
  | 'keyword_extraction'
  | 'stats_analysis'
  | 'compliance_matrix_extraction'
  | 'parsing' 
  | 'fallback' 
  | 'complete' 
  | 'error'
  | 'preprocessing'
  | 'preprocessing_complete'
  | 'generating'
  | 'extracting';
  
export interface ProposalTemplateStreamingState {
  stage: ProposalTemplateStage;
  message: string;
  template: ProposalTemplate | null;
  error: string | null;
  isStreaming: boolean;
  elapsed: number;
  retrievalTime: number;
  totalTime: number;
  evidenceCount: number;
  progress: number; // Progress percentage 0-100
}

const initialStreamingState: ProposalTemplateStreamingState = {
  stage: 'idle',
  message: '',
  template: null,
  error: null,
  isStreaming: false,
  elapsed: 0,
  retrievalTime: 0,
  totalTime: 0,
  evidenceCount: 0,
  progress: 0,
};

/**
 * Hook for streaming proposal template extraction with real-time updates
 */
export function useProposalTemplateStream() {
  const [state, setState] = useState<ProposalTemplateStreamingState>(initialStreamingState);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isStartingRef = useRef(false); // Prevent duplicate starts
  
  const startStream = useCallback((
    opportunityId: string,
    projectId: string,
    artifactIds?: string[],  // Optional artifact IDs to pass to streaming endpoint
    forceRegenerate?: boolean  // Force regeneration, bypassing cache
  ) => {
    // Cancel any existing stream first
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Prevent duplicate concurrent requests
    if (isStartingRef.current) {
      return;
    }
    
    isStartingRef.current = true;
    
    // Reset state
    setState({
      ...initialStreamingState,
      isStreaming: true,
      stage: 'started',
      message: 'Starting template extraction...',
    });
    
    // Start streaming - pass artifact IDs and force flag if provided
    abortControllerRef.current = getProposalTemplateStream(opportunityId, projectId, {
      forceRegenerate,
      onStatus: async (data: ProposalTemplateStreamStatusData) => {
        const newStage = data.stage as ProposalTemplateStage;
        
        setState(prev => ({
          ...prev,
          stage: newStage,
          message: data.message,
          elapsed: data.elapsed || prev.elapsed,
          retrievalTime: data.retrieval_time || prev.retrievalTime,
          evidenceCount: data.evidence_count || prev.evidenceCount,
          progress: data.progress || prev.progress,
        }));
        
        // Check if this is a completion stage (Phase 1 complete)
        // These stages indicate the structure extraction is done and cached
        if (newStage === 'structure_extraction_complete' || newStage === 'awaiting_approval') {
          console.log('[ProposalTemplateStream] Detected completion stage, fetching cached template...');
          
          // Fetch the cached template
          try {
            const cachedTemplate = await getProposalTemplate(opportunityId, projectId);
            
            console.log('[ProposalTemplateStream] Fetched cached template:', {
              sections: cachedTemplate.sections?.length || 0,
              questions: cachedTemplate.default_questions?.length || 0,
              source: cachedTemplate.template_source
            });
            
            setState(prev => ({
              ...prev,
              stage: 'complete',
              message: 'Template extraction complete',
              template: cachedTemplate,
              isStreaming: false,
            }));
            
            abortControllerRef.current = null;
            isStartingRef.current = false;
            
            // Close the EventSource
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
          } catch (error: any) {
            console.error('[ProposalTemplateStream] Failed to fetch cached template:', error);
            // Don't error out - continue listening for complete event
          }
        }
      },
      
      onComplete: (data: ProposalTemplateStreamCompleteData) => {
        setState(prev => ({
          ...prev,
          stage: 'complete',
          message: 'Template extraction complete',
          template: {
            sections: data.sections,
            default_questions: data.default_questions,
            template_source: data.template_source,
            entity_types_by_question: data.entity_types_by_question,
          },
          isStreaming: false,
          retrievalTime: data.retrieval_time || prev.retrievalTime,
          totalTime: data.total_time || 0,
        }));
        abortControllerRef.current = null;
        isStartingRef.current = false;
      },
      
      onError: (data: ProposalTemplateStreamErrorData) => {
        setState(prev => ({
          ...prev,
          stage: 'error',
          error: data.message,
          message: `Error: ${data.message}`,
          isStreaming: false,
        }));
        abortControllerRef.current = null;
        isStartingRef.current = false;
      },
    }, artifactIds);  // Pass artifact IDs to the stream function
  }, []);
  
  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      isStartingRef.current = false;
      setState(prev => ({
        ...prev,
        isStreaming: false,
        stage: 'idle',
        message: 'Template extraction cancelled',
      }));
    }
  }, []);
  
  const reset = useCallback(() => {
    cancelStream();
    isStartingRef.current = false;
    setState(initialStreamingState);
  }, [cancelStream]);
  
  return {
    ...state,
    startStream,
    cancelStream,
    reset,
  };
}

export function useProposalTemplate(
  opportunityId: string | null, 
  projectId: string | null,
  enabled: boolean = true
) {
  return useQuery<ProposalTemplate, Error>({
    queryKey: ['proposal-template', opportunityId, projectId],
    queryFn: () => getProposalTemplate(opportunityId!, projectId!),
    enabled: enabled && !!opportunityId && !!projectId,
  });
}

export function useProposalJobSubmit() {
  const { addToast } = useToastStore();

  return useMutation<ReportJobStatus, Error, {
    opportunityId: string;
    projectId: string;
    clientName: string;
    questions: Array<{ section: string; question: string }>;
    metadata?: {
      proposal_name?: string;
      include_past_proposals?: boolean;
      include_evidence?: boolean;
    };
  }>({
    mutationFn: async (params) => {
      return submitProposalJob(
        params.opportunityId,
        params.projectId,
        params.clientName,
        params.questions,
        params.metadata
      );
    },
    onSuccess: () => {
      addToast({
        type: 'success',
        title: 'Proposal job submitted',
        message: 'Proposal generation has started',
      });
    },
    onError: (error) => {
      addToast({
        type: 'error',
        title: 'Failed to submit proposal job',
        message: error.message,
      });
    },
  });
}

export function useProposalJobStatus(jobId: string | null, enabled: boolean = true) {
  return useQuery<ReportJobStatus, Error>({
    queryKey: ['proposal-job', jobId],
    queryFn: () => getReportJobStatus(jobId!),
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      // Poll while job is running  
      const data = query.state.data;
      if (data && ['pending', 'queued', 'running', 'checkpointed'].includes(data.status)) {
        return 3000; // Poll every 3 seconds
      }
      // When completed, do one final refetch to ensure we have the latest data
      if (data && data.status === 'completed') {
        // Refetch once more after a short delay to get final state
        setTimeout(() => {
          query.refetch();
        }, 1000);
      }
      return false; // Stop polling when complete/failed
    },
  });
}

